/**
 * Input sanitization utilities for form data.
 * Provides functions to clean and normalize user input before validation/submission.
 */
export function useSanitize() {
  /**
   * Strip HTML tags, comments, and potentially dangerous content from input
   */
  const stripHtml = (input: string): string => {
    return (
      input
        // Remove null bytes that can bypass filters
        .replace(/\0/g, '')
        // Remove HTML comments
        .replace(/<!--[\s\S]*?-->/g, '')
        // Remove CDATA sections
        .replace(/<!\[CDATA\[[\s\S]*?\]\]>/gi, '')
        // Remove script/style tags and their content
        .replace(/<(script|style)[^>]*>[\s\S]*?<\/\1>/gi, '')
        // Remove all remaining HTML tags
        .replace(/<[^>]*>/g, '')
        // Remove javascript: and other dangerous protocols
        .replace(/javascript:/gi, '')
        .replace(/vbscript:/gi, '')
        .replace(/data:/gi, '')
    )
  }

  /**
   * Escape HTML special characters for safe display
   */
  const escapeHtml = (input: string): string =>
    input
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;')

  /**
   * Trim and normalize whitespace (collapse multiple spaces to single)
   */
  const normalizeWhitespace = (input: string): string => input.trim().replace(/\s+/g, ' ')

  /**
   * Sanitize general text input (strip HTML and normalize whitespace)
   */
  const sanitizeInput = (input: string): string => normalizeWhitespace(stripHtml(input))

  /**
   * Sanitize email (lowercase and trim)
   */
  const sanitizeEmail = (input: string): string => input.trim().toLowerCase()

  /**
   * Sanitize phone number (keep only digits, plus sign, and dots)
   */
  const sanitizePhone = (input: string): string => input.replace(/[^\d+.]/g, '')

  return {
    stripHtml,
    escapeHtml,
    normalizeWhitespace,
    sanitizeInput,
    sanitizeEmail,
    sanitizePhone
  }
}
